import { useMemo } from "react";
import type { Reservation } from "@/types/reservation";
import type { Pc } from "@/types/pc";
import { dateKey, formatShortDate, formatWeekday } from "@/lib/date";
import { STATUS_COLORS, STATUS_LABELS } from "@/lib/status";

const COLUMN_WIDTH = 96;
const NAME_COLUMN_WIDTH = 140;
const ROW_HEIGHT = 56;

const VISIBLE_STATUSES = ["pending", "approved", "in_use", "overdue", "inspection", "returned"];

function diffDaysUTC(aKey: string, bKey: string): number {
  const [ay, am, ad] = aKey.split("-").map(Number);
  const [by, bm, bd] = bKey.split("-").map(Number);
  const a = Date.UTC(ay, am - 1, ad);
  const b = Date.UTC(by, bm - 1, bd);
  return Math.round((b - a) / 86400000);
}

/**
 * Independent table-based PC-by-day schedule view (no FullCalendar Scheduler
 * license required). Only the inner area scrolls horizontally.
 */
export function PcScheduleGrid({
  pcs,
  reservations,
  days,
  userNameFor,
  onSelectReservation,
}: {
  pcs: Pc[];
  reservations: Reservation[];
  days: string[];
  userNameFor: (userId: number) => string;
  onSelectReservation: (reservation: Reservation) => void;
}) {
  const rangeStart = days[0];

  const reservationsByPc = useMemo(() => {
    const map = new Map<number, Reservation[]>();
    for (const r of reservations) {
      if (!VISIBLE_STATUSES.includes(r.status)) continue;
      const list = map.get(r.pc_id) ?? [];
      list.push(r);
      map.set(r.pc_id, list);
    }
    return map;
  }, [reservations]);

  return (
    <div className="pc-schedule-scroll">
      <div style={{ minWidth: NAME_COLUMN_WIDTH + days.length * COLUMN_WIDTH }}>
        <div
          style={{
            display: "flex",
            position: "sticky",
            top: 0,
            background: "var(--color-surface)",
            zIndex: 2,
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <div
            style={{
              width: NAME_COLUMN_WIDTH,
              flexShrink: 0,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 700,
              color: "var(--color-text-muted)",
            }}
          >
            PC
          </div>
          {days.map((d) => (
            <div
              key={d}
              style={{
                width: COLUMN_WIDTH,
                flexShrink: 0,
                textAlign: "center",
                padding: "8px 4px",
                fontSize: 12,
                color: "var(--color-text-muted)",
              }}
            >
              <div>{formatShortDate(`${d}T00:00:00+09:00`)}</div>
              <div style={{ fontSize: 10 }}>{formatWeekday(`${d}T00:00:00+09:00`)}</div>
            </div>
          ))}
        </div>

        {pcs.map((pc) => {
          const pcReservations = reservationsByPc.get(pc.id) ?? [];
          return (
            <div key={pc.id} style={{ display: "flex", borderBottom: "1px solid var(--color-border)" }}>
              <div
                style={{
                  width: NAME_COLUMN_WIDTH,
                  flexShrink: 0,
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {pc.name}
              </div>
              <div
                style={{
                  position: "relative",
                  width: days.length * COLUMN_WIDTH,
                  height: ROW_HEIGHT,
                  backgroundImage: `repeating-linear-gradient(to right, var(--color-border) 0, var(--color-border) 1px, transparent 1px, transparent ${COLUMN_WIDTH}px)`,
                }}
              >
                {pcReservations.length === 0 && (
                  <span
                    style={{
                      position: "absolute",
                      left: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 12,
                      color: "var(--color-text-muted)",
                    }}
                  >
                    空き
                  </span>
                )}
                {pcReservations.map((r) => {
                  const startKey = dateKey(r.start_at);
                  const endKey = dateKey(new Date(new Date(r.end_at).getTime() - 1).toISOString());
                  let startOffset = diffDaysUTC(rangeStart, startKey);
                  let endOffset = diffDaysUTC(rangeStart, endKey);
                  if (endOffset < 0 || startOffset > days.length - 1) return null;
                  startOffset = Math.max(startOffset, 0);
                  endOffset = Math.min(endOffset, days.length - 1);
                  const left = startOffset * COLUMN_WIDTH + 4;
                  const width = (endOffset - startOffset + 1) * COLUMN_WIDTH - 8;
                  const color = STATUS_COLORS[r.status];
                  return (
                    <button
                      key={r.id}
                      onClick={() => onSelectReservation(r)}
                      title={`${userNameFor(r.user_id)} ${STATUS_LABELS[r.status]}`}
                      style={{
                        position: "absolute",
                        left,
                        width,
                        top: 10,
                        height: ROW_HEIGHT - 20,
                        background: color.bg,
                        border: `1px solid ${color.border}`,
                        color: color.fg,
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: "left",
                        padding: "0 8px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                    >
                      {userNameFor(r.user_id)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
